import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, BookOpenCheck, Loader2, Search, ShieldCheck } from "lucide-react";
import BlogCard from "../components/BlogCard";
import { PageHero } from "./About";
import { fetchPublicBlogs } from "../lib/publicBlogs";

const Blogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState(["All"]);
    const [category, setCategory] = useState("All");
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const controller = new AbortController();

        const loadBlogs = async () => {
            setLoading(true);
            setError("");

            try {
                const result = await fetchPublicBlogs(controller.signal);
                setBlogs(result.blogs);
                setCategories(result.categories);

                if (!result.blogs.length) {
                    setError("No live blogs are available right now.");
                }
            } catch (err) {
                if (err.name === "AbortError") return;
                setBlogs([]);
                setCategories(["All"]);
                setError("Live blog section is temporarily unavailable.");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadBlogs();

        return () => controller.abort();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        return blogs.filter((blog) => {
            const matchCategory = category === "All" || blog.categoryName === category;
            const searchable = [blog.title, blog.excerpt, blog.categoryName]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return matchCategory && (!q || searchable.includes(q));
        });
    }, [blogs, category, query]);

    const featured = filtered[0];
    const rest = filtered.slice(1);

    return (
        <>
            <PageHero
                eyebrow="Blogs"
                title="Fire safety knowledge for smarter facilities."
                subtitle="Explore practical VIL Engineering insights on fire protection systems, compliance, maintenance, product selection, and industrial safety readiness."
            />

            <section className="section pt-12 bg-white" data-testid="blogs-section">
                <div className="container-vil">
                    <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 mb-12 items-end">
                        <div>
                            <span className="eyebrow">/ Expert Insights</span>
                            <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                                Practical guidance for fire-safe operations.
                            </h2>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <InsightStat
                                icon={<ShieldCheck className="w-5 h-5 text-[#E53935]" />}
                                title="Compliance-first"
                                text="Focused on real site needs, codes, inspections, and maintenance routines."
                            />
                            <InsightStat
                                icon={<BookOpenCheck className="w-5 h-5 text-[#0E4DA4]" />}
                                title="Engineer-written"
                                text="Built to help owners, project teams, and facility managers decide faster."
                            />
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                type="text"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Search blogs, categories, topics..."
                                className="w-full bg-slate-50 border border-slate-200 text-slate-900 pl-11 pr-4 py-3 rounded-sm focus:border-[#E53935] focus:outline-none focus:ring-1 focus:ring-[#E53935] focus:bg-white transition placeholder:text-slate-400"
                                data-testid="blogs-search"
                            />
                        </div>

                        {loading && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading latest blogs
                            </div>
                        )}
                    </div>

                    {error && (
                        <div
                            className="mb-8 flex items-start gap-3 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                            data-testid="blogs-api-warning"
                        >
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex gap-2 overflow-x-auto pb-3 mb-10 border-b border-slate-200">
                        {categories.map((item) => (
                            <button
                                key={item}
                                onClick={() => setCategory(item)}
                                className={`px-5 py-2.5 text-sm font-mono uppercase tracking-widest whitespace-nowrap transition border ${
                                    category === item
                                        ? "bg-[#E53935] border-[#E53935] text-slate-900"
                                        : "bg-transparent border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900"
                                }`}
                                data-testid={`blogs-category-${item.replace(/\s/g, "-").toLowerCase()}`}
                            >
                                {item}
                            </button>
                        ))}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="text-center py-20 text-slate-500" data-testid="blogs-empty">
                            {blogs.length === 0
                                ? "No blogs are available right now."
                                : "No blogs match your search."}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {featured && <BlogCard blog={featured} featured />}
                            {rest.length > 0 && (
                                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                                    {rest.map((blog) => (
                                        <BlogCard key={blog.id} blog={blog} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

const InsightStat = ({ icon, title, text }) => (
    <div className="glass p-5">
        <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-slate-50 border border-slate-200">
                {icon}
            </div>
            <h3 className="font-display font-bold text-slate-900">{title}</h3>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
);

export default Blogs;
